import { useEffect, useMemo, useState } from "react";
import { BoostingCampaign } from "@vviinn/user-managment-client-ts";
import { BoostingCampaignScoreContributionStrategyEnum } from "@vviinn/user-managment-client-ts/dist/models/BoostingCampaign";

import { useAppSelector } from "../../../store";

export const boostingPresets = {
  copy: "duplicate campaign",
  custom: "manual configuration",
} as const;

export type BoostingPreset = keyof typeof boostingPresets;

const EMPTY_BOOSTING = (siteId: number, column: string): BoostingCampaign => ({
  siteId,
  name: "",
  column,
  scoreContributionStrategy: "PREFER_HIGHER_RANK",
});

export type BoostingDraft = ReturnType<typeof useBoostingDraft>;

export const useBoostingDraft = (siteId: number) => {
  const filters = useAppSelector((state) => state.campaigns.filters);
  const customFieldsTitles = useAppSelector(
    (state) => state.site.customFieldsTitles,
  );
  const boostings = useAppSelector((state) => state.boosting.boosting);
  const columnOptions = useMemo(
    () => [...filters, ...customFieldsTitles],
    [filters, customFieldsTitles],
  );

  const [name, setName] = useState("");
  const [preset, setPreset] = useState<BoostingPreset>("custom");
  const [copyFromId, setCopyFromId] = useState<string | undefined>(undefined);
  const [boostingCampaign, setBoostingCampaign] = useState<BoostingCampaign>(
    EMPTY_BOOSTING(siteId, columnOptions[0] ?? ""),
  );

  useEffect(() => {
    if (columnOptions[0] && !boostingCampaign.column) {
      setBoostingCampaign((prev) => ({ ...prev, column: columnOptions[0] }));
    }
  }, [columnOptions]);

  const onPresetChange = (value: string) => {
    const nextPreset = value as BoostingPreset;
    setPreset(nextPreset);
    if (nextPreset === "copy" && boostings.length) {
      const [first] = boostings;
      setCopyFromId(first.id);
      setBoostingCampaign({ ...first, id: undefined, siteId });
    } else {
      setCopyFromId(undefined);
      setBoostingCampaign(EMPTY_BOOSTING(siteId, columnOptions[0] ?? ""));
    }
  };

  const onCopyFromChange = (value: string) => {
    setCopyFromId(value);
    const source = boostings.find((boosting) => boosting.id === value);
    if (source) setBoostingCampaign({ ...source, id: undefined, siteId });
  };

  const setColumn = (column: string) =>
    setBoostingCampaign((prev) => ({ ...prev, column }));

  const setStrategy = (
    scoreContributionStrategy: BoostingCampaignScoreContributionStrategyEnum,
  ) => setBoostingCampaign((prev) => ({ ...prev, scoreContributionStrategy }));

  return {
    name,
    setName,
    preset,
    onPresetChange,
    copyFromId,
    onCopyFromChange,
    boostingCampaign,
    setColumn,
    setStrategy,
    columnOptions,
    boostings,
    isValid: !!name && !!boostingCampaign.column,
    payload: { ...boostingCampaign, name } as BoostingCampaign,
  };
};
